import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function SoleTraderPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-white"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <i className="ri-tools-fill text-amber-600"></i>
                <span className="text-sm font-semibold text-amber-900">Built for Solo Tradies</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Run Your Entire Trade Business <span className="text-amber-600">Solo</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                AI-powered quoting, GPS tracking, invoicing, and scheduling — all in one platform. Built specifically for sole traders who need to do it all.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-10">
                <Link 
                  to="/signup" 
                  className="px-8 py-4 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-all hover:scale-105 shadow-lg whitespace-nowrap"
                >
                  Start Free Trial <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link 
                  to="/demo" 
                  className="px-8 py-4 bg-white text-amber-600 font-semibold rounded-lg border-2 border-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap"
                >
                  Watch Demo <i className="ri-play-circle-line ml-2"></i>
                </Link>
              </div>
              
              <div className="flex items-center gap-8">
                <div>
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">Active Sole Traders</div>
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">$2.4M</div>
                  <div className="text-sm text-gray-600">Invoiced This Month</div>
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">4.9★</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-3xl transform rotate-3"></div>
              <img 
                src="https://public.readdy.ai/ai/img_res/842bf9fb3cc4f876edb8ff3582b1e368.jpg"
                alt="Sole trader using MTA Planner Pro"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need to Run Solo</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop juggling multiple apps and spreadsheets. MTA Planner Pro gives you all the tools you need in one powerful platform.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI Quoting */}
            <div className="group p-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-ai-generate text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Quoting in 60 Seconds</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Upload photos or plans. AI calculates materials, labour, and pricing. Generate professional quotes instantly.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-amber-600 mt-0.5"></i>
                  <span>Photo-to-quote AI recognition</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-amber-600 mt-0.5"></i>
                  <span>Real-time material pricing</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-amber-600 mt-0.5"></i>
                  <span>Branded PDF export</span>
                </li>
              </ul>
            </div>

            {/* GPS Tracking */}
            <div className="group p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">GPS Tracking & Routing</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Track your location, log job site visits, and optimize your daily route. Never miss a site visit again.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                  <span>Real-time location tracking</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                  <span>Automatic mileage logging</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                  <span>Smart route optimization</span>
                </li>
              </ul>
            </div>

            {/* Invoicing */}
            <div className="group p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-file-text-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Smart Invoicing</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Create, send, and track invoices in minutes. Get paid faster with automated reminders and online payments.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                  <span>One-click invoice generation</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                  <span>Automated payment reminders</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                  <span>Stripe & PayPal integration</span>
                </li>
              </ul>
            </div>

            {/* Scheduling */}
            <div className="group p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Job Scheduling</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Manage your calendar, book appointments, and never double-book again. Sync with Google Calendar.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                  <span>Drag-and-drop calendar</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                  <span>Client booking portal</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                  <span>SMS & email reminders</span>
                </li>
              </ul>
            </div>

            {/* Client Management */}
            <div className="group p-8 bg-gradient-to-br from-rose-50 to-red-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-rose-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Client Management</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Keep all client details, job history, and communications in one place. Build lasting relationships.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-rose-600 mt-0.5"></i>
                  <span>Complete client profiles</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-rose-600 mt-0.5"></i>
                  <span>Job history tracking</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-rose-600 mt-0.5"></i>
                  <span>Communication logs</span>
                </li>
              </ul>
            </div>

            {/* Mobile App */}
            <div className="group p-8 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl hover:shadow-xl transition-all hover:scale-105">
              <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-smartphone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Mobile-First Design</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Access everything from your phone or tablet. Work from the job site, not the office.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-indigo-600 mt-0.5"></i>
                  <span>Fully responsive design</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-indigo-600 mt-0.5"></i>
                  <span>Offline mode support</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-indigo-600 mt-0.5"></i>
                  <span>Photo capture & upload</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get started in minutes. No complex setup. No training required.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 relative">
            {/* Connecting Lines */}
            <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-1 bg-gradient-to-r from-amber-300 via-orange-300 to-amber-300"></div>
            
            {/* Step 1 */}
            <div className="relative text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-add-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Sign Up Free</h3>
                <p className="text-gray-600 leading-relaxed">
                  Create your account in 60 seconds. No credit card required. Start with our free trial.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-settings-3-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Set Up Your Profile</h3>
                <p className="text-gray-600 leading-relaxed">
                  Add your business details, logo, and pricing. Customize your quote and invoice templates.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Start Winning Jobs</h3>
                <p className="text-gray-600 leading-relaxed">
                  Generate quotes, schedule jobs, track time, and get paid. Run your business like a pro.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your business. Upgrade or downgrade anytime.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Trial */}
            <div className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:shadow-xl transition-all">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Trial</h3>
                <div className="text-5xl font-bold text-gray-900 mb-2">$0</div>
                <div className="text-gray-600">14 days free</div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Up to 10 quotes per month</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Basic invoicing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Calendar scheduling</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Mobile access</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-gray-400 text-xl mt-0.5"></i>
                  <span className="text-gray-400">GPS tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-gray-400 text-xl mt-0.5"></i>
                  <span className="text-gray-400">AI photo quoting</span>
                </li>
              </ul>
              
              <Link 
                to="/signup" 
                className="block w-full py-3 text-center bg-gray-100 text-gray-900 font-semibold rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Professional - Most Popular */}
            <div className="p-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-2xl transform scale-105 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-amber-600 font-bold text-sm rounded-full whitespace-nowrap">
                Most Popular
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
                <div className="text-5xl font-bold text-white mb-2">$49</div>
                <div className="text-amber-100">per month</div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">Unlimited quotes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">Unlimited invoices</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">GPS tracking & routing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">AI photo quoting</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">Client management</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-white text-xl mt-0.5"></i>
                  <span className="text-white font-medium">Priority support</span>
                </li>
              </ul>
              
              <Link 
                to="/signup" 
                className="block w-full py-3 text-center bg-white text-amber-600 font-semibold rounded-lg hover:bg-amber-50 transition-all whitespace-nowrap shadow-lg"
              >
                Get Started Now
              </Link>
            </div>

            {/* Enterprise */}
            <div className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:shadow-xl transition-all">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                <div className="text-5xl font-bold text-gray-900 mb-2">$99</div>
                <div className="text-gray-600">per month</div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Everything in Professional</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Team management (up to 5)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Advanced reporting</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Custom branding</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">API access</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Dedicated account manager</span>
                </li>
              </ul>
              
              <Link 
                to="/contact" 
                className="block w-full py-3 text-center bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all whitespace-nowrap"
              >
                Contact Sales
              </Link>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">All plans include 14-day free trial. No credit card required.</p>
            <Link to="/pricing-calculator" className="text-amber-600 font-semibold hover:text-amber-700 whitespace-nowrap">
              Calculate your ROI <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Sole Traders Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join hundreds of solo tradies who've transformed their business with MTA Planner Pro.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-amber-500 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "As a solo sparkie, I was drowning in paperwork. MTA Planner Pro cut my admin time by 70%. The AI quoting is a game-changer — I can send quotes from the job site in minutes."
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20australian%20male%20electrician%20portrait%20wearing%20work%20uniform%20smiling%20confident%20natural%20lighting%20clean%20background%20headshot%20photography&width=80&height=80&seq=testimonial001&orientation=squarish"
                  alt="Jake Morrison"
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900">Jake Morrison</div>
                  <div className="text-sm text-gray-600">Electrician, Sydney</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-amber-500 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "I've tried 4 different apps. This is the only one built for solo tradies. GPS tracking helps me log every site visit, and the invoicing is so simple my clients pay faster."
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20australian%20female%20plumber%20portrait%20wearing%20work%20clothes%20smiling%20confident%20natural%20lighting%20clean%20background%20headshot%20photography&width=80&height=80&seq=testimonial002&orientation=squarish"
                  alt="Sarah Chen"
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900">Sarah Chen</div>
                  <div className="text-sm text-gray-600">Plumber, Melbourne</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-amber-500 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "Best $49 I spend every month. I've increased my quote conversion by 40% because I can respond so fast. The mobile app means I never miss a booking."
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20australian%20male%20carpenter%20portrait%20wearing%20work%20shirt%20smiling%20confident%20natural%20lighting%20clean%20background%20headshot%20photography&width=80&height=80&seq=testimonial003&orientation=squarish"
                  alt="Tom Bradley"
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900">Tom Bradley</div>
                  <div className="text-sm text-gray-600">Carpenter, Brisbane</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Run Your Business Like a Pro?
          </h2>
          <p className="text-xl text-amber-100 mb-10 leading-relaxed">
            Join 500+ sole traders who've already made the switch. Start your free 14-day trial today — no credit card required.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <Link 
              to="/signup" 
              className="px-10 py-4 bg-white text-amber-600 font-bold text-lg rounded-lg hover:bg-amber-50 transition-all hover:scale-105 shadow-xl whitespace-nowrap"
            >
              Start Free Trial <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link 
              to="/demo" 
              className="px-10 py-4 bg-amber-700 text-white font-bold text-lg rounded-lg border-2 border-white hover:bg-amber-800 transition-all whitespace-nowrap"
            >
              Watch Demo <i className="ri-play-circle-line ml-2"></i>
            </Link>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 text-white">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}